// Wizard do trilho OFICIAL — do projeto vazio até a primeira mensagem enviada.
//
// A régua é a doutrina do produto: o cliente sai do bZapper UMA vez, pelo popup
// da Meta, e volta. Os dois passos que a Meta não expõe por API (meio de
// pagamento e verificação do negócio) são explicados aqui com o link exato,
// em vez de virarem um erro sem tradução no primeiro envio.
//
// O progresso NÃO é uma caixinha que o cliente marca: cada passo é verificado
// contra a Meta (GET /official/status), e o último é provado pelo único fato que
// não mente — uma mensagem que chegou de verdade.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowRight, BadgeCheck, Building2, Check, CreditCard, ExternalLink, History, Loader2,
  MessageCircle, Phone, Plug, RefreshCw, ShieldCheck, TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/misc';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { apiFetch, getProject } from '@/lib/api';
import { launchSignup, SignupCancelled, type SignupConfig } from '@/lib/meta';
import { useI18n, useT } from '@/lib/i18n';
import { QUALITY, limitLabel, metaLabel } from '@/lib/official-labels';

/** O que o passo de teste precisa de uma conversa (GET /conversations). */
interface Conversation {
  chat_jid: string;
  last_direction: 'in' | 'out';
  last_at: string;
}

/** Retrato do que já está pronto na Meta (GET /official/status). */
interface Readiness {
  connected: boolean;
  status?: 'PENDENTE' | 'AGUARDANDO_PAGAMENTO' | 'ATIVA' | 'SUSPENSA';
  status_reason?: string;
  display_number?: string;
  verified_name?: string;
  number_verified: boolean;
  name_status?: string;
  business_review?: string;
  /** Verificação do portfólio dono da WABA — o que a Meta exige para liberar o OTP. */
  business_verification?: string;
  owner_business_id?: string;
  owner_business_name?: string;
  currency?: string;
  quality_rating?: string;
  messaging_limit?: string;
  can_send: boolean;
  live: boolean;
  problem?: string;
}

// ackStorage guarda o aval do último passo por projeto. localStorage e não
// servidor porque é preferência de quem está olhando a tela, não estado da
// conta: a Meta é a fonte da verdade sobre o que está pronto, e ela já responde
// isso no /official/status. Toda leitura e escrita é protegida — em janela
// anônima ou com dados de site bloqueados, o acesso lança.
const ackStorage = {
  key: () => `bz_official_ack_${getProject()}`,
  get(): boolean {
    try { return localStorage.getItem(this.key()) === '1'; } catch { return false; }
  },
  set(v: boolean) {
    try {
      if (v) localStorage.setItem(this.key(), '1');
      else localStorage.removeItem(this.key());
    } catch { /* sem storage: o wizard só volta a perguntar */ }
  },
};

// O WhatsApp Manager é onde ficam os dois passos que só existem lá dentro.
const WA_MANAGER = 'https://business.facebook.com/wa/manage/';
const WA_MANAGER_BILLING = 'https://business.facebook.com/billing_hub/accounts';
const BUSINESS_VERIFICATION = 'https://business.facebook.com/settings/security';

// Rótulos de valores que vêm da Meta. O mapa é explícito de propósito: valor
// desconhecido aparece cru, em vez de virar uma chave de tradução na tela.
const NAME_STATUS = ['APPROVED', 'PENDING_REVIEW', 'REJECTED', 'NONE'] as const;
const REVIEW = ['APPROVED', 'PENDING'] as const;

// Códigos de erro que a tela sabe explicar; o resto cai numa frase honesta de
// "não deu certo" em vez de exibir o código cru ao cliente.
// Os códigos do trilho oficial vêm PREFIXADOS de `official_` (ver mapOfficialErr
// na API). Sem o prefixo nenhum deles casava: o cliente via "não foi possível
// concluir a conexão" no lugar da instrução exata, que já existia traduzida, e o
// desvio para o passo de pagamento nunca disparava.
const KNOWN_ERRORS = [
  'signup_disabled', 'signup_failed', 'missing_waba', 'missing_code',
  'multiple_phone_numbers', 'meta_permission_denied', 'invalid_access_token',
  'no_phone_numbers', 'meta_unavailable', 'official_not_connected', 'official_token_invalid',
  'official_awaiting_payment', 'official_needs_template', 'send_failed',
] as const;

function errLabel(t: (k: string) => string, code: string): string {
  return KNOWN_ERRORS.includes(code as (typeof KNOWN_ERRORS)[number])
    ? t(`official.err.${code}`)
    : t('official.err.signup_failed');
}

type StepKey = 'ready' | 'connect' | 'payment' | 'test';

export function OfficialSetup() {
  const t = useT();
  const qc = useQueryClient();
  const [ackReady, setAckReady] = useState(false);
  // O aval do passo 4 sobrevive ao recarregamento. Em memória pura, quem
  // concluísse o onboarding e desse F5 voltava ao passo de teste — e o painel da
  // conta, que é onde moram a lista de números e o botão de desconectar, sumia
  // de novo. O cliente concluía o wizard e o app fingia que ele nunca tinha
  // concluído. Por projeto, porque o aval é de um número, não da conta.
  const [paidAck, setPaidAck] = useState(() => ackStorage.get());
  const [manual, setManual] = useState(false);

  const { data: cfg } = useQuery({
    queryKey: ['official', 'signup-config'],
    queryFn: async (): Promise<SignupConfig> =>
      (await apiFetch('GET', '/official/signup-config')).data as SignupConfig,
    staleTime: 5 * 60_000,
  });

  const { data: st, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['official', 'status'],
    queryFn: async (): Promise<Readiness> =>
      (await apiFetch('GET', '/official/status')).data as Readiness,
  });

  const connect = useMutation({
    mutationFn: async () => {
      if (!cfg?.enabled) throw new Error('signup_disabled');
      const res = await launchSignup(cfg);
      // O code vale 30 segundos: daqui vai direto para o servidor trocar.
      const r = await apiFetch('POST', '/official/embedded-signup', res);
      if (!r.ok) throw new Error((r.data as { code?: string })?.code ?? 'signup_failed');
      return r.data as { warning?: string };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['official'] });
      if (data.warning) toast.warning(t(`official.warn.${data.warning}`));
      else toast.success(t('official.connectedToast'));
    },
    onError: (err: Error) => {
      if (err instanceof SignupCancelled) return; // desistir não é falha
      toast.error(errLabel(t, err.message));
    },
  });

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  const connected = !!st?.connected;
  const tokenDead = st?.problem === 'invalid_token';
  const current: StepKey = !connected || tokenDead
    ? (ackReady ? 'connect' : 'ready')
    : !paidAck && !st?.can_send
      ? 'payment'
      : 'test';

  // Conta pronta e já provada: o wizard sai de cena e dá lugar ao painel. Conta
  // SUSPENSA também fica no painel: voltar ao passo de teste escondia o motivo e
  // o botão de desconectar justamente quando a pessoa mais precisa deles.
  if (connected && paidAck && (st?.can_send || st?.status === 'SUSPENSA')) {
    return (
      <div className="flex flex-col gap-6">
        <AccountPanel st={st} onRefresh={() => refetch()} refreshing={isFetching} />
        <BusinessProfileCard st={st} />
        <ConnectedNumbers />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Steps current={current} connected={connected} />

      {current === 'ready' && <StepReady onContinue={() => setAckReady(true)} />}

      {current === 'connect' && (
        <StepConnect
          disabled={!cfg?.enabled}
          busy={connect.isPending}
          tokenDead={tokenDead}
          onConnect={() => connect.mutate()}
          onBack={() => setAckReady(false)}
          onManual={() => setManual(true)}
        />
      )}

      <ManualDialog
        open={manual}
        onOpenChange={setManual}
        onConnected={() => qc.invalidateQueries({ queryKey: ['official'] })}
      />

      {current === 'payment' && <StepPayment st={st!} onDone={() => { ackStorage.set(true); setPaidAck(true); refetch(); }} onRefresh={() => refetch()} refreshing={isFetching} />}

      {current === 'test' && (
        <StepTest
          st={st!}
          onSent={() => { ackStorage.set(true); setPaidAck(true); qc.invalidateQueries({ queryKey: ['official'] }); }}
          onSkip={() => { ackStorage.set(true); setPaidAck(true); }}
          onNeedsPayment={() => { ackStorage.set(false); setPaidAck(false); }}
        />
      )}
    </div>
  );
}

/** Trilha de passos no topo — o cliente vê onde está e o que vem depois. */
function Steps({ current, connected }: { current: StepKey; connected: boolean }) {
  const t = useT();
  const order: StepKey[] = ['ready', 'connect', 'payment', 'test'];
  const idx = order.indexOf(current);
  return (
    <ol className="flex flex-wrap items-center gap-2 text-sm">
      {order.map((k, i) => {
        const done = i < idx || (k === 'connect' && connected && current !== 'connect');
        const active = k === current;
        return (
          <li key={k} className="flex items-center gap-2">
            <span className={
              'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ' +
              (done ? 'bg-[#3c9a18]/15 text-[#3c9a18]' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')
            }>
              {done ? <Check className="size-3.5" /> : i + 1}
            </span>
            <span className={active ? 'font-medium' : 'text-muted-foreground'}>{t(`official.step.${k}`)}</span>
            {i < order.length - 1 && <ArrowRight className="size-3.5 text-muted-foreground/50" />}
          </li>
        );
      })}
    </ol>
  );
}

/** Passo 1 — o que ter em mãos antes de abrir o popup. */
function StepReady({ onContinue }: { onContinue: () => void }) {
  const t = useT();
  const items = [
    { icon: <Phone className="size-4" />, k: 'phone' },
    { icon: <Building2 className="size-4" />, k: 'company' },
    { icon: <CreditCard className="size-4" />, k: 'card' },
  ] as const;
  return (
    <Card>
      <CardContent className="flex flex-col gap-6 p-6 sm:p-8">
        <div>
          <h2 className="text-xl font-semibold">{t('official.ready.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('official.ready.subtitle')}</p>
        </div>

        <ul className="flex flex-col gap-3">
          {items.map(({ icon, k }) => (
            <li key={k} className="flex gap-3 rounded-lg border p-3">
              <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">{icon}</span>
              <div>
                <p className="text-sm font-medium">{t(`official.ready.${k}Title`)}</p>
                <p className="text-sm text-muted-foreground">{t(`official.ready.${k}Desc`)}</p>
              </div>
            </li>
          ))}
        </ul>

        <div className="rounded-lg bg-muted/40 p-4 text-sm text-muted-foreground">
          {t('official.ready.billingNote')}
        </div>

        <div className="flex justify-end">
          <Button size="lg" onClick={onContinue}>{t('official.ready.cta')} <ArrowRight className="size-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Passo 2 — o único momento fora do bZapper. */
function StepConnect({ disabled, busy, tokenDead, onConnect, onBack, onManual }: {
  disabled: boolean; busy: boolean; tokenDead: boolean;
  onConnect: () => void; onBack: () => void; onManual: () => void;
}) {
  const t = useT();
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-6 p-8 text-center sm:p-12">
        <div className="flex size-14 items-center justify-center rounded-2xl bg-[#25D366]/12">
          <MessageCircle className="size-7 text-[#128C7E]" />
        </div>
        <div className="max-w-xl">
          <h2 className="text-xl font-semibold">{t(tokenDead ? 'official.connect.reconnectTitle' : 'official.connect.title')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t(tokenDead ? 'official.connect.reconnectSubtitle' : 'official.connect.subtitle')}
          </p>
        </div>

        <ol className="grid w-full max-w-2xl gap-3 text-left sm:grid-cols-3">
          {(['inside1', 'inside2', 'inside3'] as const).map((k, i) => (
            <li key={k} className="rounded-lg border bg-muted/30 p-3">
              <span className="text-xs font-semibold text-primary">{i + 1}</span>
              <p className="mt-1 text-sm">{t(`official.connect.${k}`)}</p>
            </li>
          ))}
        </ol>

        <div className="flex flex-col items-center gap-2">
          <Button size="lg" disabled={disabled || busy} onClick={onConnect}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Plug className="size-4" />}
            {t('official.connect.cta')}
          </Button>
          <p className="text-xs text-muted-foreground">{t('official.connect.hint')}</p>
        </div>

        {disabled && (
          <p className="flex items-center gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-500">
            <TriangleAlert className="size-3.5 shrink-0" /> {t('official.signupDisabled')}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-center gap-4">
          <button type="button" onClick={onBack} className="text-xs text-muted-foreground underline-offset-2 hover:underline">
            {t('official.connect.back')}
          </button>
          <button type="button" onClick={onManual} className="text-xs text-muted-foreground underline-offset-2 hover:underline">
            {t('official.manualLink')}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Passo 3 — o que a Meta não deixa fazer por API. */
function StepPayment({ st, onDone, onRefresh, refreshing }: {
  st: Readiness; onDone: () => void; onRefresh: () => void; refreshing: boolean;
}) {
  const t = useT();
  return (
    <Card>
      <CardContent className="flex flex-col gap-6 p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <BadgeCheck className="size-5 text-[#3c9a18]" />
          <p className="text-sm">
            {t('official.payment.connected', { number: st.display_number || t('official.numberPending') })}
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold">{t('official.payment.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('official.payment.subtitle')}</p>
        </div>

        <ol className="flex flex-col gap-3">
          {(['p1', 'p2', 'p3'] as const).map((k, i) => (
            <li key={k} className="flex gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">{i + 1}</span>
              <p className="text-sm">{t(`official.payment.${k}`)}</p>
            </li>
          ))}
        </ol>

        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <a href={WA_MANAGER_BILLING} target="_blank" rel="noopener noreferrer">
              {t('official.payment.openBilling')} <ExternalLink className="size-3.5" />
            </a>
          </Button>
          <Button variant="ghost" disabled={refreshing} onClick={onRefresh}>
            {refreshing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            {t('official.recheck')}
          </Button>
        </div>

        <div className="rounded-lg bg-muted/40 p-4 text-sm text-muted-foreground">{t('official.payment.note')}</div>

        <div className="flex justify-end border-t pt-4">
          <Button onClick={onDone}>{t('official.payment.cta')} <ArrowRight className="size-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Passo 4 — a prova. Uma mensagem que chega vale mais que qualquer checklist. */
// StepTest — a prova final: uma mensagem que chegou de verdade.
//
// O cliente manda PRIMEIRO, e o wizard responde. Parece uma inversão estranha,
// mas é a única ordem que a Meta permite: fora da janela de 24h o trilho oficial
// só transmite template aprovado, e uma WABA recém-criada não tem nenhum — nem o
// hello_world. A versão anterior mandava texto puro para um número frio, então
// falhava SEMPRE, com um 422 que o wizard ainda por cima traduzia errado.
//
// A mensagem do cliente abre a janela; a resposta prova os dois sentidos de uma
// vez — o webhook de entrada chegou e o envio saiu. Uma redonda melhor que a
// original, que só provava a saída.
function StepTest({ st, onSent, onSkip, onNeedsPayment }: {
  st: Readiness; onSent: () => void; onSkip: () => void; onNeedsPayment: () => void;
}) {
  const t = useT();
  const [sent, setSent] = useState(false);
  // Só conversas que chegaram DEPOIS de abrir o passo contam: um histórico
  // antigo do mesmo número daria o passo por concluído sem nada ter acontecido.
  const [since] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);

  const reply = useMutation({
    mutationFn: async (to: string) => {
      const r = await apiFetch('POST', '/messages/text', { to, body: t('official.test.body') });
      if (!r.ok) throw new Error((r.data as { code?: string })?.code ?? 'send_failed');
    },
    onSuccess: () => { setSent(true); onSent(); },
    onError: (err: Error) => {
      // A recusa da Meta por falta de pagamento é o caso mais comum aqui — e o
      // wizard devolve o cliente ao passo que resolve, em vez de só falhar.
      if (err.message === 'official_awaiting_payment' || err.message === 'official_not_connected') {
        onNeedsPayment();
        toast.error(t('official.test.needsPayment'));
        return;
      }
      toast.error(errLabel(t, err.message));
    },
  });

  // Espera a mensagem do cliente. Para de perguntar assim que ela chega (ou
  // assim que a resposta sai), em vez de bater na API para sempre.
  const inbound = useQuery({
    queryKey: ['official', 'test-inbound'],
    queryFn: async () => {
      const r = await apiFetch('GET', '/conversations');
      const rows = ((r.data as { data?: Conversation[] })?.data ?? []);
      return rows.find((c) => c.last_direction === 'in' && Date.parse(c.last_at) >= since) ?? null;
    },
    refetchInterval: sent || reply.isPending ? false : 3000,
    enabled: !sent,
  });

  const waiting = inbound.data ?? null;
  const number = st.display_number ?? '';

  // Chegou: responde UMA vez. O disparo mora num efeito, não no corpo do
  // componente — durante o render ele rodaria a cada refetch da espera e
  // enfileiraria um envio atrás do outro. `replied` é o trinco.
  const replied = useRef(false);
  useEffect(() => {
    if (!waiting || replied.current) return;
    replied.current = true;
    reply.mutate(waiting.chat_jid.split('@')[0]);
  }, [waiting, reply]);

  if (sent) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center sm:p-12">
          <div className="flex size-14 items-center justify-center rounded-2xl bg-[#3c9a18]/12">
            <Check className="size-7 text-[#3c9a18]" />
          </div>
          <h2 className="text-xl font-semibold">{t('official.done.title')}</h2>
          <p className="max-w-lg text-sm text-muted-foreground">{t('official.done.subtitle')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-6 p-6 sm:p-8">
        <div>
          <h2 className="text-xl font-semibold">{t('official.test.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('official.test.subtitle')}</p>
        </div>

        <div className="flex flex-col gap-2 sm:max-w-sm">
          <Label>{t('official.test.numberLabel')}</Label>
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
            <Phone className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 font-mono text-base font-semibold">{number || '—'}</span>
            <Button
              type="button" variant="ghost" size="sm" disabled={!number}
              onClick={() => {
                navigator.clipboard?.writeText(number).then(
                  () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
                  () => toast.error(t('official.test.copyFailed')),
                );
              }}
            >
              {copied ? <Check className="size-4 text-[#3c9a18]" /> : t('official.test.copy')}
            </Button>
          </div>
        </div>

        <ol className="flex flex-col gap-3">
          {(['p1', 'p2', 'p3'] as const).map((k, i) => (
            <li key={k} className="flex gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">{i + 1}</span>
              <p className="text-sm">{t(`official.test.${k}`)}</p>
            </li>
          ))}
        </ol>

        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {reply.isPending ? t('official.test.replying') : t('official.test.waiting')}
          </span>
          {st.messaging_limit && <Badge variant="secondary">{t('official.limit')}: {limitLabel(t, st.messaging_limit)}</Badge>}
        </div>

        {/* A regra é da Meta, não nossa — dizer isso com todas as letras evita que
            a espera pareça o bZapper travado, que foi exatamente como ela soou. */}
        <div className="flex gap-3 rounded-lg bg-muted/40 p-4 text-sm text-muted-foreground">
          <ShieldCheck className="mt-0.5 size-4 shrink-0" />
          <p>{t('official.test.why')}</p>
        </div>

        {/* Esperar não pode prender o resto: a conta JÁ está utilizável, e este
            passo é confirmação, não pré-requisito. */}
        <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">{t('official.test.skipHint')}</p>
          <Button variant="outline" onClick={onSkip}>
            {t('official.test.skip')} <ArrowRight className="size-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Depois do wizard: o estado da conta, sempre verificável. */
function AccountPanel({ st, onRefresh, refreshing }: { st: Readiness; onRefresh: () => void; refreshing: boolean }) {
  const t = useT();
  const [disconnecting, setDisconnecting] = useState(false);
  const qc = useQueryClient();

  const disconnect = async () => {
    if (!window.confirm(t('official.confirmDisconnect'))) return;
    setDisconnecting(true);
    try {
      const r = await apiFetch('DELETE', '/official/account');
      if (r.ok) { toast.success(t('official.disconnected')); qc.invalidateQueries({ queryKey: ['official'] }); }
      else toast.error(t('official.disconnectFailed'));
    } finally { setDisconnecting(false); }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex size-12 items-center justify-center rounded-xl bg-[#25D366]/12">
              <MessageCircle className="size-6 text-[#128C7E]" />
            </div>
            <div>
              <p className="text-lg font-semibold">{st.display_number || t('official.numberPending')}</p>
              <p className="text-sm text-muted-foreground">{st.verified_name || t('official.noVerifiedName')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={st.can_send ? 'success' : 'warning'}>
              {t(st.can_send ? 'official.readyBadge' : `official.status.${st.status}`)}
            </Badge>
            <Button variant="ghost" size="sm" disabled={refreshing} onClick={onRefresh}>
              {refreshing ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
            </Button>
          </div>
        </div>

        {st.status === 'SUSPENSA' && (
          <div className="flex gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div>
              <p className="font-medium">{t('official.suspended.title')}</p>
              <p className="mt-1 text-muted-foreground">{t('official.suspended.body')}</p>
              {st.status_reason && <p className="mt-2 font-mono text-xs text-muted-foreground">{st.status_reason}</p>}
            </div>
          </div>
        )}

        {!st.live && (
          <p className="flex items-center gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-500">
            <TriangleAlert className="size-3.5 shrink-0" /> {t('official.stale')}
          </p>
        )}

        <dl className="grid gap-4 border-t pt-4 sm:grid-cols-4">
          <Field label={t('official.quality')} value={metaLabel(t, QUALITY, 'official.qualityValue', st.quality_rating)} />
          <Field label={t('official.limit')} value={limitLabel(t, st.messaging_limit)} />
          <Field label={t('official.nameStatus')} value={metaLabel(t, NAME_STATUS, 'official.nameStatusValue', st.name_status)} />
          <Field label={t('official.businessReview')} value={metaLabel(t, REVIEW, 'official.businessReviewValue', st.business_review)} />
        </dl>

        {/* Conta em dólar: a Meta migra as contas do Brasil para real (prazo
            30/06/2027). Até lá, a estimativa em reais do painel é aproximada. */}
        {st.currency && st.currency !== 'BRL' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-amber-500/10 p-4 text-sm">
            <span>{t('official.currencyNotice', { currency: st.currency })}</span>
            <Button asChild variant="outline" size="sm">
              <a href="https://business.facebook.com/billing_hub" target="_blank" rel="noopener noreferrer">
                {t('official.currencyCta')} <ExternalLink className="size-3.5" />
              </a>
            </Button>
          </div>
        )}

        {/* Portfólio não verificado: a conta pode estar aprovada e mesmo assim a Meta
            nunca liberar o código de verificação. O Embedded Signup deixa criar um
            portfólio novo, sem CNPJ, que não passa — e ninguém percebia. */}
        {st.business_verification && st.business_verification !== 'verified' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-amber-500/10 p-4 text-sm">
            <span className="flex items-start gap-2">
              <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
              {t('official.portfolioUnverified', {
                name: st.owner_business_name || t('official.portfolioNoName'),
                status: t(`templates.roles.otp.verify.${st.business_verification}`, {}),
              })}
            </span>
            <Button asChild variant="outline" size="sm">
              <a href={st.owner_business_id
                ? `https://business.facebook.com/latest/settings/business_info?business_id=${st.owner_business_id}`
                : BUSINESS_VERIFICATION} target="_blank" rel="noopener noreferrer">
                {t('official.portfolioUnverifiedCta')} <ExternalLink className="size-3.5" />
              </a>
            </Button>
          </div>
        )}

        {st.business_review && st.business_review !== 'APPROVED' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted/40 p-4 text-sm">
            <span>{t('official.verifyBusiness')}</span>
            <Button asChild variant="outline" size="sm">
              <a href={BUSINESS_VERIFICATION} target="_blank" rel="noopener noreferrer">
                {t('official.verifyBusinessCta')} <ExternalLink className="size-3.5" />
              </a>
            </Button>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="size-3.5" /> {t('official.tokenSafe')}
          </p>
          <div className="flex gap-2">
            <Button asChild variant="ghost" size="sm">
              <a href={WA_MANAGER} target="_blank" rel="noopener noreferrer">
                {t('official.openManager')} <ExternalLink className="size-3.5" />
              </a>
            </Button>
            <Button variant="ghost" size="sm" className="text-destructive" disabled={disconnecting} onClick={disconnect}>
              {disconnecting ? <Loader2 className="size-3.5 animate-spin" /> : null} {t('official.disconnect')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Perfil comercial do número (GET/PUT /official/profile). */
interface BusinessProfile {
  about?: string;
  address?: string;
  description?: string;
  email?: string;
  vertical?: string;
  websites?: string[];
  profile_picture_url?: string;
}

// Setores aceitos pela Meta. Lista fechada: qualquer outro valor ela recusa, e o
// servidor barra antes de enviar.
const VERTICALS = [
  'UNDEFINED', 'OTHER', 'AUTO', 'BEAUTY', 'APPAREL', 'EDU', 'ENTERTAIN',
  'EVENT_PLAN', 'FINANCE', 'GROCERY', 'GOVT', 'HOTEL', 'HEALTH', 'NONPROFIT',
  'PROF_SERVICES', 'RETAIL', 'TRAVEL', 'RESTAURANT', 'NOT_A_BIZ',
] as const;

/**
 * Perfil comercial — o cartão de visita que o cliente final vê ao abrir a
 * conversa.
 *
 * Era o último motivo para abrir o WhatsApp Manager, e por isso está aqui: a
 * régua do produto é que depois do Embedded Signup nada mais aconteça fora do
 * bZapper. Os limites de tamanho são conferidos antes de ir à Meta, porque a
 * recusa dela é um "(#100) Invalid parameter" que não diz qual campo estourou.
 */
function BusinessProfileCard({ st }: { st: Readiness }) {
  const t = useT();
  const qc = useQueryClient();
  const [form, setForm] = useState<BusinessProfile | null>(null);

  // Pedir a troca do nome NÃO é chamada de API — é ação do WhatsApp Manager. O
  // que nos cabe é o passo seguinte, que as pessoas esquecem: até o número ser
  // re-registrado, ele continua exibindo o nome antigo mesmo já aprovado.
  const reregister = useMutation({
    mutationFn: async () => {
      const r = await apiFetch('POST', '/official/profile/reregister');
      if (!r.ok) {
        const body = r.data as { code?: string; message?: string };
        throw new Error(body?.message || errLabel(t, body?.code ?? 'signup_failed'));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['official'] });
      toast.success(t('official.profile.reregistered'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['official', 'profile'],
    queryFn: async (): Promise<BusinessProfile> =>
      ((await apiFetch('GET', '/official/profile')).data as BusinessProfile) ?? {},
  });

  // O formulário nasce do que veio da Meta, e só uma vez: recarregar por baixo do
  // cliente que está digitando apagaria o que ele escreveu.
  const current = form ?? data ?? {};
  const set = (k: keyof BusinessProfile, v: string | string[]) =>
    setForm({ ...current, [k]: v });

  // A foto é rota própria (multipart): trocar o e-mail não deve reenviar a
  // imagem inteira, e a Meta a recebe pelo upload reenviável, não como JSON.
  const photo = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const r = await apiFetch('POST', '/official/profile/photo', fd);
      if (!r.ok) {
        const body = r.data as { code?: string; message?: string };
        throw new Error(body?.message || errLabel(t, body?.code ?? 'signup_failed'));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['official', 'profile'] });
      toast.success(t('official.profile.photoSaved'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const save = useMutation({
    mutationFn: async () => {
      const r = await apiFetch('PUT', '/official/profile', {
        about: current.about ?? '',
        address: current.address ?? '',
        description: current.description ?? '',
        email: current.email ?? '',
        vertical: current.vertical ?? '',
        websites: (current.websites ?? []).filter(Boolean),
      });
      if (!r.ok) {
        const body = r.data as { code?: string; message?: string };
        // A mensagem do servidor é específica ("a frase tem 154 e o limite é
        // 139"); usá-la crua vale mais que um rótulo genérico nosso.
        throw new Error(body?.message || errLabel(t, body?.code ?? 'signup_failed'));
      }
    },
    onSuccess: () => {
      setForm(null);
      qc.invalidateQueries({ queryKey: ['official', 'profile'] });
      toast.success(t('official.profile.saved'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const sites = current.websites ?? [];
  const dirty = form !== null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-5 p-6">
        <div>
          <h3 className="font-medium">{t('official.profile.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('official.profile.subtitle')}</p>
        </div>

        {/* Uma imagem só, redonda: o WhatsApp Business não tem capa. */}
        <div className="flex items-center gap-4">
          {current.profile_picture_url ? (
            <img src={current.profile_picture_url} alt=""
              className="size-16 shrink-0 rounded-full object-cover" />
          ) : (
            <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-muted">
              <MessageCircle className="size-6 text-muted-foreground" />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="inline-flex">
              <input type="file" accept="image/jpeg,image/png" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = ''; // permite reenviar o mesmo arquivo
                  if (f) photo.mutate(f);
                }} />
              <span className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted">
                {photo.isPending && <Loader2 className="size-4 animate-spin" />}
                {t('official.profile.changePhoto')}
              </span>
            </label>
            <p className="text-xs text-muted-foreground">{t('official.profile.photoHint')}</p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>{t('official.profile.about')}</Label>
            <Input value={current.about ?? ''} maxLength={139}
              onChange={(e) => set('about', e.target.value)} />
            <p className="text-xs text-muted-foreground">
              {(current.about ?? '').length}/139 · {t('official.profile.aboutHint')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>{t('official.profile.description')}</Label>
            <Input value={current.description ?? ''} maxLength={512}
              onChange={(e) => set('description', e.target.value)} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('official.profile.email')}</Label>
            <Input type="email" value={current.email ?? ''}
              onChange={(e) => set('email', e.target.value)} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('official.profile.vertical')}</Label>
            <select
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={current.vertical ?? ''}
              onChange={(e) => set('vertical', e.target.value)}
            >
              <option value="">{t('official.profile.verticalNone')}</option>
              {VERTICALS.map((v) => (
                <option key={v} value={v}>{t(`official.profile.verticals.${v}`)}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>{t('official.profile.address')}</Label>
            <Input value={current.address ?? ''} maxLength={256}
              onChange={(e) => set('address', e.target.value)} />
          </div>

          {/* Dois campos fixos porque dois é o teto da Meta: oferecer um botão de
              "adicionar site" só levaria à recusa dela no terceiro. */}
          {[0, 1].map((i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <Label>{t('official.profile.website')} {i + 1}</Label>
              <Input value={sites[i] ?? ''} placeholder="https://"
                onChange={(e) => {
                  const next = [sites[0] ?? '', sites[1] ?? ''];
                  next[i] = e.target.value;
                  set('websites', next);
                }} />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          {dirty && (
            <Button variant="ghost" onClick={() => setForm(null)}>{t('official.profile.discard')}</Button>
          )}
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending && <Loader2 className="size-4 animate-spin" />}
            {t('official.profile.save')}
          </Button>
        </div>

        {/* O nome fica APARTADO do resto: os campos acima valem na hora, e este
            abre uma análise da Meta com trava de 30 dias entre pedidos. No mesmo
            formulário, corrigir o e-mail dispararia a análise sem querer. */}
        <div className="flex flex-col gap-3 rounded-lg border border-dashed p-4">
          <div>
            <Label>{t('official.profile.displayName')}</Label>
            <p className="mt-1 text-sm font-medium">{st.verified_name || t('official.noVerifiedName')}</p>
          </div>
          <p className="text-xs text-muted-foreground">{t('official.profile.displayNameHint')}</p>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={WA_MANAGER} target="_blank" rel="noopener noreferrer">
                {t('official.profile.requestName')} <ExternalLink className="size-3.5" />
              </a>
            </Button>
            <Button variant="ghost" size="sm" disabled={reregister.isPending}
              onClick={() => reregister.mutate()}>
              {reregister.isPending && <Loader2 className="size-4 animate-spin" />}
              {t('official.profile.reregister')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Um número da WABA ainda não conectado (GET /official/numbers/available). */
interface AvailableNumber {
  id: string;
  display_phone_number?: string;
}

/** Um número conectado do projeto (GET /official/numbers). */
interface ConnectedNumber {
  id: string;
  display_number?: string;
  verified_name?: string;
  status: string;
  quality_rating?: string;
  messaging_limit?: string;
  is_default: boolean;
  instance_id?: string;
}

/**
 * Números conectados — a Meta permite vários por conta comercial.
 *
 * Aparece SEMPRE, mesmo com um número só. Antes se escondia abaixo de dois, e o
 * resultado era circular: como o único jeito de adicionar o segundo é este
 * painel, ele nunca chegava a existir e a lista nunca aparecia. A funcionalidade
 * estava construída e trancada por trás da própria condição de exibição.
 *
 * O `instance_id` fica visível porque é o mesmo valor que se informa no envio
 * para escolher o número — exatamente como no trilho não oficial.
 */
/** Cada mudança de qualidade e limite do número, da mais recente para a mais antiga. */
function NumberHealth({ id }: { id: string }) {
  const { t, locale } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ['official-number-health', id],
    queryFn: async () => ((await apiFetch('GET', `/official/numbers/${id}/health`)).data as
      { history?: { quality_rating: string; messaging_limit: string; at: string }[] })?.history ?? [],
  });
  if (isLoading) return <Loader2 className="size-4 basis-full animate-spin text-muted-foreground" />;
  if (!data?.length) return <p className="basis-full text-xs text-muted-foreground">{t('official.numbers.historyEmpty')}</p>;
  return (
    <ul className="basis-full divide-y rounded-md bg-muted/30 text-xs">
      {data.map((p, i) => (
        <li key={i} className="flex flex-wrap gap-x-3 gap-y-0.5 px-3 py-1.5">
          <span className="tabular-nums text-muted-foreground">{new Date(p.at).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })}</span>
          {p.quality_rating && <span>{t('official.quality')}: <strong>{metaLabel(t, QUALITY, 'official.qualityValue', p.quality_rating)}</strong></span>}
          {p.messaging_limit && <span>{t('official.limit')}: <strong>{limitLabel(t, p.messaging_limit)}</strong></span>}
        </li>
      ))}
    </ul>
  );
}

function ConnectedNumbers() {
  const t = useT();
  const qc = useQueryClient();
  const { data: numbers = [] } = useQuery({
    queryKey: ['official', 'numbers'],
    queryFn: async (): Promise<ConnectedNumber[]> =>
      ((await apiFetch('GET', '/official/numbers')).data as { numbers?: ConnectedNumber[] })?.numbers ?? [],
  });

  const setDefault = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiFetch('PUT', `/official/numbers/${id}/default`);
      if (!r.ok) throw new Error(t('official.numbers.defaultFailed'));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['official'] });
      toast.success(t('official.numbers.defaultSaved'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Números da WABA que ainda não entraram no projeto. Só é consultado quando o
  // cliente abre o seletor: é uma ida à Meta, não vale pagá-la em todo carregamento.
  const [adding, setAdding] = useState(false);
  const [historyOf, setHistoryOf] = useState<string | null>(null);
  const available = useQuery({
    queryKey: ['official', 'numbers', 'available'],
    enabled: adding,
    queryFn: async (): Promise<AvailableNumber[]> =>
      ((await apiFetch('GET', '/official/numbers/available')).data as { numbers?: AvailableNumber[] })?.numbers ?? [],
  });

  const add = useMutation({
    mutationFn: async (phoneNumberId: string) => {
      const r = await apiFetch('POST', '/official/numbers', { phone_number_id: phoneNumberId });
      if (!r.ok) throw new Error(errLabel(t, (r.data as { code?: string })?.code ?? 'signup_failed'));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['official'] });
      setAdding(false);
      toast.success(t('official.numbers.added'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiFetch('DELETE', `/official/numbers/${id}`);
      if (!r.ok) throw new Error(t('official.numbers.removeFailed'));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['official'] });
      toast.success(t('official.numbers.removed'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-6">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-medium">{t('official.numbers.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('official.numbers.subtitle')}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setAdding((v) => !v)}>
            <Plug className="size-4" /> {t('official.numbers.add')}
          </Button>
        </div>

        {adding && (
          <div className="flex flex-col gap-2 rounded-lg border border-dashed p-3">
            {available.isLoading ? (
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> {t('official.numbers.loading')}
              </span>
            ) : available.data?.length ? (
              <>
                <p className="text-sm">{t('official.pickNumber')}</p>
                {available.data.map((n) => (
                  <Button key={n.id} variant="outline" className="justify-start" disabled={add.isPending}
                    onClick={() => add.mutate(n.id)}>
                    {n.display_phone_number || n.id}
                  </Button>
                ))}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t('official.numbers.noneAvailable')}</p>
            )}
          </div>
        )}

        <ul className="flex flex-col gap-2">
          {numbers.map((n) => (
            <li key={n.id} className="flex flex-wrap items-center gap-2 rounded-lg border p-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{n.display_number || t('official.numberPending')}</p>
                <p className="truncate font-mono text-[11px] text-muted-foreground">{n.instance_id}</p>
              </div>
              {n.quality_rating && <Badge variant="secondary">{t('official.quality')}: {metaLabel(t, QUALITY, 'official.qualityValue', n.quality_rating)}</Badge>}
              <Button size="sm" variant="ghost" onClick={() => setHistoryOf(historyOf === n.id ? null : n.id)}>
                <History className="size-3.5" />{t('official.numbers.history')}
              </Button>
              <span className="ml-auto flex items-center gap-2">
                {n.is_default ? (
                  <Badge variant="success">{t('official.numbers.isDefault')}</Badge>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setDefault.mutate(n.id)}>
                      {t('official.numbers.makeDefault')}
                    </Button>
                    {/* O padrão não some por engano: para tirá-lo, promova outro antes. */}
                    <Button size="sm" variant="ghost" disabled={remove.isPending}
                      onClick={() => { if (window.confirm(t('official.numbers.confirmRemove'))) remove.mutate(n.id); }}>
                      {t('official.numbers.remove')}
                    </Button>
                  </>
                )}
              </span>
              {historyOf === n.id && <NumberHealth id={n.id} />}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium">{value}</dd>
    </div>
  );
}

/**
 * Conexão manual — a saída de emergência do suporte, para quando o popup falha
 * num cliente específico. Fica atrás de um link discreto de propósito: colar
 * credenciais não é a experiência do produto.
 *
 * Mesmo aqui ninguém digita o id do número: com a conta e o token, o servidor
 * descobre. Só quando a conta tem mais de um número é que aparece a escolha —
 * como lista de botões, não como campo.
 */
function ManualDialog({ open, onOpenChange, onConnected }: {
  open: boolean; onOpenChange: (v: boolean) => void; onConnected: () => void;
}) {
  const t = useT();
  const [wabaId, setWabaId] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [numbers, setNumbers] = useState<{ id: string; display_phone_number?: string }[]>([]);

  const submit = async (phoneNumberId?: string) => {
    setBusy(true);
    try {
      const r = await apiFetch('POST', '/official/account', {
        waba_id: wabaId.trim(),
        access_token: token.trim(),
        phone_number_id: phoneNumberId,
        status: 'ATIVA',
      });
      if (r.ok) {
        toast.success(t('official.connectedToast'));
        onOpenChange(false);
        setWabaId(''); setToken(''); setNumbers([]);
        onConnected();
        return;
      }
      const body = r.data as { code?: string; numbers?: { id: string; display_phone_number?: string }[] };
      if (body?.code === 'multiple_phone_numbers' && body.numbers?.length) {
        setNumbers(body.numbers);
        return;
      }
      toast.error(errLabel(t, body?.code ?? 'signup_failed'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('official.manualTitle')}</DialogTitle>
          <DialogDescription>{t('official.manualDesc')}</DialogDescription>
        </DialogHeader>

        {numbers.length > 0 ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm">{t('official.pickNumber')}</p>
            {numbers.map((n) => (
              <Button key={n.id} variant="outline" disabled={busy} onClick={() => submit(n.id)} className="justify-start">
                {n.display_phone_number || n.id}
              </Button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>{t('official.wabaId')}</Label>
              <Input value={wabaId} onChange={(e) => setWabaId(e.target.value)} className="font-mono" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('official.token')}</Label>
              <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} className="font-mono" />
              <p className="text-xs text-muted-foreground">{t('official.tokenHint')}</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          {numbers.length === 0 && (
            <Button disabled={busy || !wabaId.trim() || !token.trim()} onClick={() => submit()}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null} {t('official.manualConnect')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
